import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from './Icon';
import BrandLogo from './BrandLogo';
import { useChat } from '../chat/ChatContext';
import '../css/sidebar.css';

/**
 * Боковая навигация, разбитая по блокам. Свёрнутое состояние живёт в
 * localStorage; на узких экранах сайдбар превращается в выдвижную панель.
 */
const Sidebar = ({ user, onLogout, allowedRoles, open, onClose, collapsed, onToggleCollapsed }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadTotal } = useChat();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const sections = useMemo(() => [
    {
      id: 'workspace',
      label: t('nav.sections.workspace', { defaultValue: 'Разделы' }),
      items: [
        { path: '/home', label: t('nav.home'), icon: 'home' },
        { path: '/orders', label: t('nav.orders'), icon: 'orders' },
        { path: '/users', label: t('nav.users'), icon: 'users' },
        { path: '/complaints', label: t('nav.complaints'), icon: 'complaints' },
        { path: '/logs', label: t('nav.logs', { defaultValue: 'Логи' }), icon: 'database' },
      ],
      roles: [allowedRoles.SUPPORT, allowedRoles.MODERATOR, allowedRoles.ADMIN, allowedRoles.DEV],
    },
    {
      id: 'tools',
      label: t('nav.sections.tools', { defaultValue: 'Инструменты' }),
      items: [
        {
          path: '/chat',
          label: t('nav.chat', { defaultValue: 'Чат' }),
          icon: 'message-square',
          badge: unreadTotal,
          roles: [allowedRoles.SUPPORT, allowedRoles.MODERATOR, allowedRoles.ADMIN, allowedRoles.DEV],
        },
        {
          path: '/calendar',
          label: t('nav.calendar', { defaultValue: 'Календарь' }),
          icon: 'calendar',
          roles: [allowedRoles.SUPPORT, allowedRoles.MODERATOR, allowedRoles.ADMIN, allowedRoles.DEV],
        },
        {
          path: '/statistics',
          label: t('nav.statistics', { defaultValue: 'Статистика' }),
          icon: 'bar-chart-2',
          roles: [allowedRoles.ADMIN, allowedRoles.DEV],
        },
      ],
    },
    {
      id: 'admin',
      label: t('nav.sections.admin', { defaultValue: 'Администрирование' }),
      // Разделы админки раскрыты пунктами меню — вкладки внутри страницы больше не нужны.
      items: [
        {
          path: '/admin?tab=stats',
          label: t('admin.dashboard.stats'),
          icon: 'bar-chart-2',
          roles: [allowedRoles.MODERATOR, allowedRoles.ADMIN, allowedRoles.DEV],
        },
        {
          path: '/admin?tab=push',
          label: t('admin.dashboard.notifications'),
          icon: 'bell',
          roles: [allowedRoles.ADMIN, allowedRoles.DEV],
        },
        {
          path: '/admin?tab=create',
          label: t('admin.dashboard.createEmployee'),
          icon: 'user-plus',
          roles: [allowedRoles.ADMIN, allowedRoles.DEV],
        },
        {
          path: '/admin?tab=promo',
          label: t('admin.dashboard.promoCodes'),
          icon: 'tag',
          roles: [allowedRoles.ADMIN, allowedRoles.DEV],
        },
        {
          path: '/admin?tab=marketplace',
          label: t('admin.dashboard.marketplace'),
          icon: 'shopping-cart',
          roles: [allowedRoles.ADMIN, allowedRoles.DEV],
        },
        {
          path: '/admin?tab=banners',
          label: t('admin.dashboard.banners'),
          icon: 'image',
          roles: [allowedRoles.DEV],
        },
        {
          path: '/admin?tab=public-files',
          label: t('admin.dashboard.publicFiles'),
          icon: 'upload',
          roles: [allowedRoles.DEV],
        },
        {
          path: '/admin?tab=delete-user',
          label: t('admin.dashboard.deleteUser'),
          icon: 'trash-2',
          roles: [allowedRoles.DEV],
        },
      ],
    },
  ], [t, allowedRoles, unreadTotal]);

  // Роль может быть задана на секции целиком или на отдельном пункте.
  const visibleSections = sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => (item.roles || section.roles || []).includes(user?.role)),
    }))
    .filter((section) => section.items.length > 0);

  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [userMenuOpen]);

  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  const go = (path) => {
    navigate(path);
    onClose();
  };

  // Пункты админки различаются query-параметром tab, поэтому сверяем и его.
  const isItemActive = (path) => {
    const [pathname, search] = path.split('?');
    if (location.pathname !== pathname) return false;
    if (!search) return true;
    const target = new URLSearchParams(search).get('tab');
    const current = new URLSearchParams(location.search).get('tab') || 'stats';
    return target === current;
  };

  const roleClass = {
    [allowedRoles.DEV]: 'dev',
    [allowedRoles.ADMIN]: 'admin',
    [allowedRoles.MODERATOR]: 'moderator',
    [allowedRoles.SUPPORT]: 'support',
  }[user?.role] || '';

  return (
    <>
      {/* Затемнение под выдвинутой панелью — только на узких экранах */}
      {open && <div className="sb-overlay" onClick={onClose} role="presentation" />}

      <aside className={`sb${collapsed ? ' sb--collapsed' : ''}${open ? ' sb--open' : ''}`}>
        <div className="sb-head">
          <div className="sb-brand">
            <BrandLogo className="sb-logo" />
            {!collapsed && (
              <div className="sb-brand-texts">
                <span className="sb-brand-name">TeSalut!</span>
                <span className="sb-brand-sub">Admin Panel</span>
              </div>
            )}
          </div>
        </div>

        <nav className="sb-nav">
          {visibleSections.map((section) => (
            <div className="sb-section" key={section.id}>
              {!collapsed && <span className="sb-section-label">{section.label}</span>}
              {section.items.map((item) => {
                const active = isItemActive(item.path);
                return (
                  <button
                    key={item.path}
                    type="button"
                    className={`sb-item${active ? ' active' : ''}`}
                    onClick={() => go(item.path)}
                    title={collapsed ? item.label : undefined}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span className="sb-item-icon">
                      <Icon name={item.icon} size={18} />
                    </span>
                    {!collapsed && <span className="sb-item-label">{item.label}</span>}
                    {item.badge > 0 && (
                      <span className={`sb-badge${collapsed ? ' sb-badge--dot' : ''}`}>
                        {collapsed ? '' : item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sb-foot" ref={userMenuRef}>
          <button
            type="button"
            className="sb-user"
            onClick={() => setUserMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
          >
            <span className="sb-user-avatar">
              {(user?.fullName || user?.phoneNumber || '?').trim().charAt(0).toUpperCase()}
            </span>
            {!collapsed && (
              <span className="sb-user-texts">
                <span className="sb-user-name">
                  {user?.fullName || user?.phoneNumber || t('nav.userFallback')}
                </span>
                <span className={`sb-user-role ${roleClass}`}>{user?.role}</span>
              </span>
            )}
            {!collapsed && <Icon name="chevron-up" size={16} />}
          </button>

          <AnimatePresence>
            {userMenuOpen && (
              <motion.div
                className="sb-user-menu"
                role="menu"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
              >
                {/* Язык переключается глобусом в верхней полосе */}
                <button type="button" className="sb-user-menu-item logout" onClick={onLogout}>
                  <Icon name="log-out" size={18} />
                  {t('nav.logout', { defaultValue: 'Выйти' })}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
